import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import FeaturedDeals from '../components/FeaturedDeals';
import { previewProduct, registerAndTrack, getPublicStats } from '../services/api';
import { PALETTE, CHART_POINTS, CHART_TARGET, HERO_PLACEHOLDERS, looksLikeSearchQuery, timeAgo, alertTickerLine, FOOTER_COLS, COMPARISON_ROWS, WATCHLIST_ROWS } from '../components/landing/content';
import { PriceChart, EmailPreview, FlowStep, FAQ } from '../components/landing/marketing';
import { TrackerModal } from '../components/landing/TrackerModal';
import { RetailerStrip } from '../components/landing/RetailerStrip';
import './Landing.css';
// Sections and the signup modal live in ../components/landing/ (split
// 2026-09-24; this was a 1,020-line file).

// ── Landing ───────────────────────────────────────────────────────────────
export default function Landing() {
  const howItWorksRef = useRef(null);
  const faqRef        = useRef(null);
  const dealsRef      = useRef(null);

  // Wizard state
  const [wizUrl, setWizUrl]                     = useState('');
  const [wizStep, setWizStep]                   = useState(0);
  const [wizProduct, setWizProduct]             = useState(null);
  const [wizTarget, setWizTarget]               = useState('');
  const [wizAnyDrop, setWizAnyDrop]             = useState(false);
  const [wizEmail, setWizEmail]                 = useState('');
  const [wizPassword, setWizPassword]           = useState('');
  const [wizShowPw, setWizShowPw]               = useState(false);
  const [wizCreateAccount, setWizCreateAccount] = useState(true);
  const [wizError, setWizError]                 = useState('');
  const [wizSearchHint, setWizSearchHint]       = useState(false);
  const [wizLoading, setWizLoading]             = useState(false);
  const [wizEmailOnly, setWizEmailOnly]         = useState(false);

  // Hero cycling placeholder
  const [phIdx, setPhIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPhIdx(i => (i + 1) % HERO_PLACEHOLDERS.length), 3200);
    return () => clearInterval(id);
  }, []);

  // Hero mockup tab
  const [mockupTab, setMockupTab] = useState('dashboard');

  // Live counter
  const [liveStats, setLiveStats] = useState(null);
  useEffect(() => {
    getPublicStats()
      .then(r => setLiveStats(r.data.data))
      .catch(() => {});
  }, []);

  const handleUrlSubmit = useCallback(async (e, overrideUrl) => {
    if (e && e.preventDefault) e.preventDefault();
    const rawUrl = overrideUrl || wizUrl;
    const trimmed = rawUrl.trim();
    if (!trimmed) return;
    if (looksLikeSearchQuery(trimmed)) {
      setWizError('');
      setWizSearchHint(true);
      return;
    }
    setWizUrl(rawUrl);
    setWizError('');
    setWizSearchHint(false);
    setWizStep(1);
    try {
      const res = await previewProduct(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
      const p = res.data.data;
      setWizProduct(p);
      if (p.price) { setWizTarget((p.price * 0.9).toFixed(2)); setWizAnyDrop(false); }
      else { setWizTarget(''); setWizAnyDrop(true); } // out of stock: a back-in-stock watch
      setWizStep(2);
    } catch (err) {
      setWizError(err.response?.data?.error?.message || 'Could not fetch that product. Please check the URL and try again.');
      setWizStep(0);
    }
  }, [wizUrl]);

  const handleTrackThis = useCallback((url) => {
    handleUrlSubmit(null, url);
  }, [handleUrlSubmit]);

  const handleTrack = async () => {
    setWizError('');
    setWizLoading(true);
    try {
      const res = await registerAndTrack({
        email: wizEmail.trim(),
        password: wizCreateAccount && wizPassword ? wizPassword : undefined,
        create_account: wizCreateAccount,
        product: wizProduct,
        target_price: wizAnyDrop ? null : (wizTarget ? parseFloat(wizTarget) : null),
      });
      const { token } = res.data.data;
      setWizEmailOnly(!token);
      setWizStep(4);
      if (token) {
        localStorage.setItem('token', token);
        setTimeout(() => { window.location.href = '/dashboard'; }, 1800);
      }
    } catch (err) {
      const code = err.response?.data?.error?.code;
      if (code === 'EMAIL_EXISTS') {
        setWizError('That email already has an account. Sign in to add products to your watchlist.');
      } else {
        setWizError(err.response?.data?.error?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setWizLoading(false);
    }
  };

  const closeModal = () => {
    setWizStep(0);
    setWizUrl('');
    setWizProduct(null);
    setWizTarget('');
    setWizAnyDrop(false);
    setWizEmail('');
    setWizPassword('');
    setWizShowPw(false);
    setWizError('');
    setWizSearchHint(false);
    setWizEmailOnly(false);
    setWizCreateAccount(true);
    setWizLoading(false);
  };

  const scrollTo = (ref) => (e) => {
    e.preventDefault();
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="l-page">

      {/* ── Nav ── */}
      <header className="l-nav">
        <div className="l-nav-logo">
          <div className="l-nav-logo-badge">P</div>
          <span>Pricehuntify</span>
        </div>
        <nav className="l-nav-links">
          <a href="#how-it-works" onClick={scrollTo(howItWorksRef)}>How it works</a>
          <a href="#live-deals" onClick={scrollTo(dealsRef)}>Live deals</a>
          <a href="#faq" onClick={scrollTo(faqRef)}>FAQ</a>
        </nav>
        <div className="l-nav-actions">
          <Link to="/login" className="l-nav-signin">Sign in</Link>
          <Link to="/register" className="l-nav-cta">Get started — free</Link>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="l-hero">
        <div className="l-hero-copy">
          <div className="l-hero-badge">
            <span className="l-hero-badge-dot" />
            Tracking 9 Amazon storefronts worldwide
          </div>
          <h1 className="l-hero-h1">
            Free Amazon price tracker.<br />
            <em>Start saving</em> on autopilot.
          </h1>
          <p className="l-hero-sub">
            Drop in any Amazon URL, name your price, and we'll email the moment it drops. No app, no card, no nonsense.
          </p>

          <form className="l-url-form" onSubmit={handleUrlSubmit}>
            <input
              type="text"
              inputMode="url"
              value={wizUrl}
              onChange={e => setWizUrl(e.target.value)}
              placeholder={HERO_PLACEHOLDERS[phIdx]}
              className="l-url-input"
              spellCheck={false}
            />
            <button type="submit" className="l-url-btn">Start tracking &#x2192;</button>
          </form>
          {wizError && wizStep === 0 && <div className="l-wiz-error">{wizError}</div>}
          {wizSearchHint && wizStep === 0 && (
            <div className="l-search-hint">
              That looks like a product search, not a link.{' '}
              <Link to="/register">Create a free account</Link> to search Amazon by
              name — or paste a product URL here.
            </div>
          )}

          {/* Live counter strip */}
          <div className="l-live-counter">
            <span className="l-live-counter-dot" />
            <span>
              <strong>{liveStats ? liveStats.active_trackers.toLocaleString() : '—'}</strong>
              {' '}trackers running
            </span>
            <span className="l-live-counter-sep" />
            <span>
              <strong style={{ color: PALETTE.orange }}>{liveStats ? liveStats.alerts_today : '—'}</strong>
              {' '}alerts fired today
            </span>
          </div>

          <div className="l-hero-trust">
            <span>&#x2713; Free forever</span>
            <span>&#x2713; No credit card</span>
            <span>&#x2713; 60-second setup</span>
          </div>
        </div>

        {/* Right column: switchable mockup */}
        <div className="l-hero-mockup-wrap">
          <div className="l-mockup-tabs">
            <button
              className={`l-mockup-tab${mockupTab === 'dashboard' ? ' l-mockup-tab--on' : ''}`}
              onClick={() => setMockupTab('dashboard')}
            >01 · Watchlist</button>
            <button
              className={`l-mockup-tab${mockupTab === 'email' ? ' l-mockup-tab--on' : ''}`}
              onClick={() => setMockupTab('email')}
            >02 · The email</button>
            <span className="l-mockup-tab-label">
              {mockupTab === 'dashboard' ? 'YOUR WATCHLIST' : 'WHAT LANDS IN YOUR INBOX'}
            </span>
          </div>

          {mockupTab === 'dashboard' ? (
            <div className="l-hero-mockup">
              <div className="l-mockup-titlebar">
                <span>my watchlist · 12 products</span>
                <span className="l-mockup-alert">● 3 alerts today</span>
              </div>
              <div className="l-mockup-featured">
                <div className="l-mockup-featured-top">
                  <div>
                    <div className="l-mockup-product-name">Sony WH-1000XM5 · amazon.com</div>
                    <div className="l-mockup-price-row">
                      <span className="l-mockup-price">$155.00</span>
                      <span className="l-mockup-delta">&#x2193; 22% from list</span>
                    </div>
                  </div>
                  <div className="l-mockup-target">
                    <div className="l-mockup-target-label">YOUR TARGET</div>
                    <div className="l-mockup-target-value">$160.00</div>
                  </div>
                </div>
                <div className="l-mockup-chart">
                  <PriceChart points={CHART_POINTS} target={CHART_TARGET} width={520} height={160} />
                </div>
              </div>
              <table className="l-mockup-table">
                <thead>
                  <tr><th>PRODUCT</th><th>NOW</th><th>TGT</th><th>&#x394;</th></tr>
                </thead>
                <tbody>
                  {WATCHLIST_ROWS.map(([name, now, tgt, delta, color], i) => (
                    <tr key={i}>
                      <td>{name}</td>
                      <td className="l-mono">{now}</td>
                      <td className="l-mono l-muted">{tgt}</td>
                      <td className="l-mono" style={{ color, fontWeight: 700 }}>{delta}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="l-hero-mockup">
              <EmailPreview />
            </div>
          )}
        </div>
      </section>

      {/* ── Retailer comparison strip ── */}
      <RetailerStrip />

      {/* ── Featured deals ── */}
      <div ref={dealsRef}>
        <FeaturedDeals onTrackThis={handleTrackThis} />
      </div>

      {/* ── How it works ── */}
      <section className="l-how" id="how-it-works" ref={howItWorksRef}>
        <div className="l-how-header">
          <div className="l-section-label">HOW IT WORKS</div>
          <h2>One pipe, four checkpoints</h2>
        </div>
        <div className="l-flow-steps">
          <FlowStep
            n="01"
            title={<>You paste URL or search<span className="l-reg-star" title="Search available with a free registered account">&nbsp;*</span></>}
            desc="Any Amazon link, any region — or search products by name."
          />
          <span className="l-flow-arrow">&#x2192;</span>
          <FlowStep n="02" title="Set target $" desc="Or skip — alert on any drop." />
          <span className="l-flow-arrow">&#x2192;</span>
          <FlowStep n="03" title="Daily sweep" desc="Worker re-checks every 24h." />
          <span className="l-flow-arrow">&#x2192;</span>
          <FlowStep n="04" title="Email arrives" desc="Within 60s of price hit." />
        </div>
        {liveStats?.last_alert && (
          <div className="l-how-ticker">
            <span className="l-how-ticker-dot" />
            <span className="l-how-ticker-label">LAST ALERT FIRED</span>
            <span className="l-how-ticker-sep">·</span>
            <strong>{timeAgo(liveStats.last_alert.ago_seconds)}</strong>
            <span className="l-how-ticker-sep">·</span>
            <span>{alertTickerLine(liveStats.last_alert)}</span>
          </div>
        )}
      </section>

      {/* ── The math ── */}
      <section className="l-math" id="math">
        <div className="l-math-header">
          <div className="l-section-label">THE MATH</div>
          <h2>Same shopper, two months</h2>
          <p>Real averages from 800+ users with ≥5 tracked items.</p>
        </div>
        <div className="l-math-table-wrap">
          <table className="l-math-table">
            <thead>
              <tr>
                <th className="l-math-th-scenario">SCENARIO</th>
                <th>Without Pricehuntify</th>
                <th className="l-math-th-with">With Pricehuntify</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map(([scenario, without, withVal], i) => (
                <tr key={i}>
                  <td className="l-math-scenario">{scenario}</td>
                  <td className="l-mono l-muted l-center">{without}</td>
                  <td className="l-mono l-center l-math-with-val">{withVal}</td>
                </tr>
              ))}
              <tr className="l-math-total-row">
                <td>2-month total saved</td>
                <td className="l-mono l-center l-math-total-blank">—</td>
                <td className="l-mono l-center l-math-total-val">+$455</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="l-math-footnote l-mono">
          ↑ Median user. 90th percentile saves $1,100+ over the same window.
        </div>
      </section>

      {/* ── FAQ ── */}
      <div ref={faqRef}>
        <FAQ />
      </div>

      {/* ── CTA ── */}
      <section className="l-cta">
        <h2 className="l-cta-h2">
          Track a product. <span className="l-cta-accent">Right now.</span>
        </h2>
        <p className="l-cta-sub">No download. No card. Paste an Amazon URL and you're tracking.</p>
        <form className="l-url-form l-url-form--dark" onSubmit={handleUrlSubmit}>
          <input
            type="text"
            inputMode="url"
            value={wizUrl}
            onChange={e => setWizUrl(e.target.value)}
            placeholder="https://amazon.com/dp/..."
            className="l-url-input"
            spellCheck={false}
          />
          <button type="submit" className="l-url-btn">Start tracking &#x2192;</button>
        </form>
        {wizSearchHint && wizStep === 0 && (
          <div className="l-search-hint l-search-hint--dark">
            That looks like a product search, not a link.{' '}
            <Link to="/register">Create a free account</Link> to search Amazon by
            name — or paste a product URL here.
          </div>
        )}
        <div className="l-cta-trust l-mono">
          ✓ FREE FOREVER · ✓ NO CARD · ✓ EMAIL ONLY WHEN YOUR TARGET HITS
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="l-footer">
        <div className="l-footer-inner">
          <div className="l-footer-brand">
            <div className="l-footer-brand-row">
              <div className="l-nav-logo-badge">P</div>
              <span className="l-footer-brand-name">Pricehuntify</span>
            </div>
            <p className="l-footer-brand-desc">
              An independent Amazon price-drop alert service. We don't sell your data.
            </p>
            <div className="l-footer-status l-mono">● ALL SYSTEMS NORMAL</div>
          </div>
          {FOOTER_COLS.map(col => (
            <div key={col.h} className="l-footer-col">
              <div className="l-footer-col-head l-mono">{col.h}</div>
              <ul>
                {col.links.map((link, i) => (
                  <li key={i}>
                    {typeof link === 'string'
                      ? <span style={{ opacity: 0.45 }}>{link}</span>
                      : link.href
                        ? <a href={link.href}>{link.label}</a>
                        : <Link to={link.to}>{link.label}</Link>
                    }
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="l-footer-note l-mono">
          <span className="l-reg-star">*</span> Available to registered users —{' '}
          <Link to="/register">create a free account</Link>.
        </div>
        <div className="l-footer-bottom">
          <span>&#x00A9; 2026 Pricehuntify · Independent · Not sponsored or endorsed by Amazon.com, Inc.</span>
          <span className="l-mono">built in Sydney 🇦🇺</span>
        </div>
      </footer>

      {/* ── Modal ── */}
      {wizStep > 0 && (
        <TrackerModal
          step={wizStep}
          url={wizUrl}
          product={wizProduct}
          target={wizTarget}
          anyDrop={wizAnyDrop}
          email={wizEmail}
          password={wizPassword}
          showPw={wizShowPw}
          createAccount={wizCreateAccount}
          loading={wizLoading}
          error={wizError}
          emailOnly={wizEmailOnly}
          onClose={closeModal}
          onSetTarget={setWizTarget}
          onSetAnyDrop={setWizAnyDrop}
          onSetEmail={setWizEmail}
          onSetPassword={setWizPassword}
          onToggleShowPw={() => setWizShowPw(p => !p)}
          onSetCreateAccount={setWizCreateAccount}
          onConfirm={() => setWizStep(3)}
          onTrack={handleTrack}
          onBack={() => setWizStep(2)}
        />
      )}

    </div>
  );
}
